import { BehaviorSubject, distinctUntilChanged, first, skip, Subject } from "rxjs";
import { Logger } from "../../src/02_utils/logger";
import { Utils } from "../../src/02_utils/utils";
import { ErcasFilter } from "../ercas-filter/filter";
import { SearchInputService } from "./services/inputService";
import { ISearchModel, ISearchRequest, SearchRequest } from "../../src/05_components/search/dto/requestBody.dto";
import { SearchRequestService } from "./services/requestService";
import { Tab } from "bootstrap";
import { IFilterRequest } from "../../src/05_components/filter/dto/filter.dto";
import { UrlService } from "./services/urlService";


export class ErcasSearch {
  private elements: {
    filters: NodeListOf<HTMLElement>,
    tabs: HTMLElement
  }
  private filters: {[key: string]: ErcasFilter} = {};
  private searchRequest: BehaviorSubject<ISearchRequest>;
  private searchResponse: BehaviorSubject<ISearchModel> = new BehaviorSubject({});
  private error: Subject<Error> = new Subject();
  private loading: Subject<boolean> = new Subject();

  private excludeSearchParams: Array<string> = [
    'pimcore_preview',
    '_dc',
  ];


  constructor(private search: HTMLElement, private $logger: Logger, private $utils: Utils) {
    this.initElements();
    this.initSearchRequest();

    new SearchInputService(search, this.$utils, this.searchRequest, this.error, this.filters);
    new SearchRequestService(search, this.searchRequest, this.searchResponse, this.loading, this.error);
    new UrlService(search, this.searchRequest);

    this.searchResponse.pipe(
      skip(2),
      distinctUntilChanged()
    ).subscribe((response) => {
      let tabActive: boolean = false;
      this.$utils.addOrRemoveClases('remove', 'd-none', this.elements.tabs);

      Object.keys(response)?.forEach(key => {
        const value = response[key];

        if (value.total != 0 && tabActive == false) {
          this.activateTab(key);
          tabActive = true;
        }

        this.setTotal(response, key);

        this.filters[key].filterResponse.next(value);
      })
    });

    const filters = this.getFilters();
    if (filters) {
      const newSearchRequest: ISearchRequest = {
        ...this.searchRequest.getValue(),
        filters: filters
      };

      this.searchRequest.next(newSearchRequest);
    }
  }

  private initSearchRequest() {
    const searchRequest = new SearchRequest();
    const documentId = parseInt(this.search.dataset.document ?? '');
    searchRequest.document = documentId;
    this.initFilters(searchRequest);

    this.searchRequest = new BehaviorSubject(searchRequest);
  }

  private initFilters(searchRequest: ISearchRequest) {
    this.elements?.filters?.forEach(filter => {
      const key = filter.dataset.key;
      this.filters[key] = new ErcasFilter(filter, this.$logger, this.$utils, true);

      this.filters[key].filterRequest.pipe(
        first()
      ).subscribe((request) => {
        searchRequest.fields[key] = request.fields;
        searchRequest.nextPage[key] = request.nextPage;
        searchRequest.filterDefinitions.push({
          id: request.filterDefinition,
          key: key
        });
      });

      this.filters[key].filterRequest.pipe(
        skip(1)
      ).subscribe(request => {
        const newNextPage = this.searchRequest.getValue().nextPage;
        newNextPage[key] = request.nextPage;
        const newSearchRequest: ISearchRequest = {
          ...this.searchRequest.getValue(),
          nextPage: newNextPage,
        };

        this.searchRequest.next(newSearchRequest);
      });

      this.loading.subscribe((loading) => {
        this.filters[key].loading.next(loading);
      });
    });
  }

  private getFilters(): {[key: string]: string} {
    const params = new URLSearchParams(window.location.search);
    const currentFilters: Map<string, any> = new Map();

    if (this.excludeSearchParams.find(x => params.get(x)) || !params) {
      return {};
    }

    params.forEach((value, key) => {
      if (key.endsWith('[]')) {
        currentFilters.set(key.replace('[]', ''), value.includes(',') ? value.split(',') : [value]);
      } else {
        currentFilters.set(key, value);
      }
    });

    return Object.fromEntries(currentFilters);
  }

  private activateTab(key: string) {
    const activeTab = this.search.querySelector(`button[data-bs-target][aria-selected="true"]`);
    if (activeTab && (activeTab as any).disabled === false) {
      return;
    }

    const triggerEl = this.search.querySelector(`button[data-bs-target="#${key}"]`);
    if (triggerEl) {
      const tab = Tab.getOrCreateInstance(triggerEl);
      tab?.show();
    }
  }

  private setTotal(response: ISearchModel, key: string) {
    const totals = this.search.querySelectorAll(`[data-total][data-key="${key}"]`);

    totals?.forEach(total => {
      total.innerHTML = response[key]?.pagination?.totalCount?.toString();
    });

    const button: HTMLButtonElement = this.search.querySelector(`#${key}-tab`);

    if (response[key]?.pagination?.totalCount == 0) {
      button.disabled = true;
    } else {
      button.disabled = false;
    }
  }

  private initElements() {
    this.elements = {
      filters: this.search.querySelectorAll('.js-search-filter'),
      tabs: this.search.querySelector('.js-tabs')
    }
  }
}
